import axios from "axios";

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3000/api/";
const CLEAN_URL = RAW_BACKEND_URL.replace(/\/api\/?$/, "").replace(/\/$/, "");
const BACKEND_URL = CLEAN_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: false,
});

// Bearer token fallback for environments where the HttpOnly cookie isn't sent
api.interceptors.request.use((cfg) => {
  const tok = localStorage.getItem("td_session");
  if (tok && !cfg.headers.Authorization) {
    cfg.headers.Authorization = `Bearer ${tok}`;
  }
  return cfg;
});

// Normalize errors so toasts/banners never render a pydantic ValidationError object
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const d = err?.response?.data?.detail;
    if (Array.isArray(d)) {
      err.response.data.detail = d.map((x) => (x?.msg ? `${(x.loc || []).slice(-1)[0] || "field"}: ${x.msg}` : String(x))).join(" · ");
    } else if (d && typeof d === "object") {
      err.response.data.detail = d?.msg || JSON.stringify(d);
    }
    return Promise.reject(err);
  }
);

// ---------------- AUTH ----------------
export const signup = (payload) =>
  api.post("/api/auth/signup", payload).then((r) => {
    if (r.data?.access_token) localStorage.setItem("td_session", r.data.access_token);
    return r.data;
  });
export const acceptInvitation = (token) =>
  api.post("/api/auth/accept-invitation", { token }).then((r) => r.data);
export const resendInvitation = (email) =>
  api.post("/api/auth/resend-invitation", { email }).then((r) => r.data);
export const login = (payload) =>
  api.post("/api/auth/login", payload).then((r) => {
    if (r.data?.access_token) localStorage.setItem("td_session", r.data.access_token);
    return r.data;
  });
export const forgotPassword = (email) =>
  api.post("/api/auth/forgot-password", { email }).then((r) => r.data);
export const resetPassword = (payload) =>
  api.post("/api/auth/reset-password", payload).then((r) => r.data);
export const changePassword = (payload) =>
  api.post("/api/auth/change-password", payload).then((r) => {
    if (r.data?.access_token) localStorage.setItem("td_session", r.data.access_token);
    return r.data;
  });
export const logout = () =>
  api.post("/api/auth/logout").then((r) => {
    localStorage.removeItem("td_session");
    return r.data;
  });
export const me = () => api.get("/api/auth/me").then((r) => r.data);
export const myOrg = () => api.get("/api/auth/me/org").then((r) => r.data);
export const fetchMemberships = () => api.get("/api/auth/memberships").then((r) => r.data);
export const switchOrg = (organizationId) =>
  api.post("/api/auth/switch-org", { organization_id: organizationId }).then((r) => {
    if (r.data?.access_token) localStorage.setItem("td_session", r.data.access_token);
    return r.data;
  });
export const exchangeGoogleSession = (sessionId) =>
  api.post("/api/auth/google/session", null, { headers: { "X-Session-ID": sessionId } }).then((r) => {
    if (r.data?.access_token) localStorage.setItem("td_session", r.data.access_token);
    return r.data;
  });

// ---------------- ORG ----------------
export const updateOrg = (data) => api.patch("/api/orgs/me", data).then((r) => r.data);

// ---------------- PROGRAMS ----------------
export const listPrograms = () => api.get("/api/programs").then((r) => r.data);
export const createProgram = (data) => api.post("/api/programs", data).then((r) => r.data);
export const updateProgram = (id, data) => api.patch(`/api/programs/${id}`, data).then((r) => r.data);
export const deleteProgram = (id) => api.delete(`/api/programs/${id}`).then((r) => r.data);

// ---------------- CATEGORIES ----------------
export const listCategories = () => api.get("/api/categories").then((r) => r.data);
export const createCategory = (data) => api.post("/api/categories", data).then((r) => r.data);

// ---------------- MEMBERS ----------------
export const listMembers = () => api.get("/api/members").then((r) => r.data);
export const inviteMember = (data) => api.post("/api/members/invite", data).then((r) => r.data);
export const bulkUploadMembers = (items) => api.post("/api/members/bulk-upload", { items }).then((r) => r.data);
export const updateMemberRole = (id, role) =>
  api.patch(`/api/members/${id}/role`, { role }).then((r) => r.data);
export const updateMember = (id, data) =>
  api.patch(`/api/members/${id}`, data).then((r) => r.data);
export const deleteMember = (id) => api.delete(`/api/members/${id}`).then((r) => r.data);

// ---------------- DONORS & VOLUNTEERS BULK UPLOAD ----------------
export const bulkUploadDonors = (items) => api.post("/api/donors/bulk-upload", { items }).then((r) => r.data);
export const bulkUploadVolunteers = (items) => api.post("/api/volunteers/bulk-upload", { items }).then((r) => r.data);

// ---------------- OCR ----------------
export const ocrReceipt = (image_data_url) =>
  api.post("/api/receipts/ocr", { image_data_url }).then((r) => r.data);

// ---------------- EXPENSES ----------------
export const listExpenses = (params = {}) =>
  api.get("/api/expenses", { params }).then((r) => r.data);
export const getExpense = (id) => api.get(`/api/expenses/${id}`).then((r) => r.data);
export const createExpense = (data) => api.post("/api/expenses", data).then((r) => r.data);
export const updateExpense = (id, data) => api.patch(`/api/expenses/${id}`, data).then((r) => r.data);
export const deleteExpense = (id) => api.delete(`/api/expenses/${id}`).then((r) => r.data);
export const restoreExpense = (id) => api.post(`/api/expenses/${id}/restore`).then((r) => r.data);
export const expenseActivity = (id) => api.get(`/api/expenses/${id}/activity`).then((r) => r.data);
export const approveExpense = (id, note) =>
  api.post(`/api/expenses/${id}/approve`, { note }).then((r) => r.data);
export const rejectExpense = (id, reason) =>
  api.post(`/api/expenses/${id}/reject`, { reason }).then((r) => r.data);
export const requestInfo = (id, question) =>
  api.post(`/api/expenses/${id}/request-info`, { question }).then((r) => r.data);
export const markPaid = (id, data) =>
  api.post(`/api/expenses/${id}/mark-paid`, data).then((r) => r.data);
export const exportExpensesCsvUrl = (flavor = "trustdime") =>
  `${API_BASE}/expenses/export.csv?flavor=${encodeURIComponent(flavor)}`;

// ---------------- STREAMS (Grants / Fund Streams) ----------------
export const listStreams = () => api.get("/api/streams").then((r) => r.data);
export const createStream = (data) => api.post("/api/streams", data).then((r) => r.data);
export const updateStream = (id, data) => api.patch(`/api/streams/${id}`, data).then((r) => r.data);
export const deleteStream = (id) => api.delete(`/api/streams/${id}`).then((r) => r.data);
export const streamReport = (id) => api.get(`/api/streams/${id}/report`).then((r) => r.data);
export const streamReportPdfUrl = (id) => `${API_BASE}/streams/${id}/report.pdf`;
export const streamReportCsvUrl = (id) => `${API_BASE}/streams/${id}/report.csv`;

// ---------------- MFA ----------------
export const mfaStatus = () => api.get("/api/auth/mfa/status").then((r) => r.data);
export const mfaSetup = () => api.post("/api/auth/mfa/setup").then((r) => r.data);
export const mfaVerify = (code) => api.post("/api/auth/mfa/verify", { code }).then((r) => r.data);
export const mfaDisable = (code) => api.post("/api/auth/mfa/disable", { code }).then((r) => r.data);
export const mfaChallenge = (mfa_token, code) =>
  api.post("/api/auth/mfa/challenge", { mfa_token, code }).then(async (r) => {
    if (r.data?.access_token) localStorage.setItem("td_session", r.data.access_token);
    return r.data;
  });

// ---------------- Passkeys ----------------
export const passkeysConfig = () => api.get("/api/auth/passkeys/config").then((r) => r.data);
export const listPasskeys = () => api.get("/api/auth/passkeys").then((r) => r.data);
export const passkeyDelete = (id) => api.delete(`/api/auth/passkeys/${id}`).then((r) => r.data);
export const passkeyRegisterBegin = () => api.post("/api/auth/passkeys/register/begin").then((r) => r.data);
export const passkeyRegisterComplete = (label, credential) =>
  api.post("/api/auth/passkeys/register/complete", { label, credential }).then((r) => r.data);
export const passkeyAuthBegin = (email) =>
  api.post("/api/auth/passkeys/authenticate/begin", { email }).then((r) => r.data);
export const passkeyAuthComplete = (email, credential) =>
  api.post("/api/auth/passkeys/authenticate/complete", { email, credential }).then(async (r) => {
    if (r.data?.access_token) localStorage.setItem("td_session", r.data.access_token);
    return r.data;
  });
export const gmailStatus = () => api.get("/api/gmail/status").then((r) => r.data);
export const gmailConnectUrl = () => api.get("/api/gmail/connect").then((r) => r.data);
export const gmailDisconnect = () => api.post("/api/gmail/disconnect").then((r) => r.data);

// ---------------- BOARD ----------------
export const boardMetrics = () => api.get("/api/dashboard/board").then((r) => r.data);

// ---------------- BILLING ----------------
export const listPlans = () => api.get("/api/billing/plans").then((r) => r.data);
export const createCheckout = (plan, origin_url) =>
  api.post("/api/billing/checkout", { plan, origin_url }).then((r) => r.data);
export const checkoutStatus = (sessionId) =>
  api.get(`/api/billing/status/${sessionId}`).then((r) => r.data);

// ---------------- ORG profile ----------------
export const uploadOrgLogo = (logo_data_url) => api.put("/api/orgs/me/logo", { logo_data_url }).then((r) => r.data);
export const removeOrgLogo = () => api.delete("/api/orgs/me/logo").then((r) => r.data);

// ---------------- PUBLIC ----------------
export const publicOrg = (slug) => api.get(`/api/public/orgs/${slug}`).then((r) => r.data);

// ---------------- MISSION CONTROL (platform admins) ----------------
export const mcOverview = () => api.get("/api/mission-control/overview").then((r) => r.data);
export const mcListOrgs = (params = {}) => api.get("/api/mission-control/orgs", { params }).then((r) => r.data);
export const mcUpdateOrg = (id, data) => api.patch(`/api/mission-control/orgs/${id}`, data).then((r) => r.data);
export const mcDisableOrg = (id) => api.post(`/api/mission-control/orgs/${id}/disable`).then((r) => r.data);
export const mcEnableOrg = (id) => api.post(`/api/mission-control/orgs/${id}/enable`).then((r) => r.data);
export const mcImpersonateOrg = (id) => api.post(`/api/mission-control/orgs/${id}/impersonate`).then((r) => r.data);
export const mcListPlatformUsers = () => api.get("/api/mission-control/users").then((r) => r.data);
export const mcCreatePlatformUser = (data) => api.post("/api/mission-control/users", data).then((r) => r.data);
export const mcListPricing = () => api.get("/api/mission-control/pricing").then((r) => r.data);
export const mcCreatePricing = (data) => api.post("/api/mission-control/pricing", data).then((r) => r.data);
export const mcUpdatePricing = (code, data) => api.patch(`/api/mission-control/pricing/${code}`, data).then((r) => r.data);
export const mcDeletePricing = (code) => api.delete(`/api/mission-control/pricing/${code}`).then((r) => r.data);
export const mcListPromos = () => api.get("/api/mission-control/promos").then((r) => r.data);
export const mcCreatePromo = (data) => api.post("/api/mission-control/promos", data).then((r) => r.data);
export const mcDeletePromo = (code) => api.delete(`/api/mission-control/promos/${code}`).then((r) => r.data);
export const mcStripeConfig = () => api.get("/api/mission-control/stripe/config").then((r) => r.data);
export const mcStripeUpdate = (data) => api.post("/api/mission-control/stripe/config", data).then((r) => r.data);
export const mcEnforceMfa = (policy, region) =>
  api.post("/api/mission-control/mfa/enforce", null, { params: { policy, ...(region && { region }) } }).then((r) => r.data);
export const mcBulkUpload = (data) => api.post("/api/mission-control/bulk-upload", data).then((r) => r.data);
export const mcGetPendingOrgs = () => api.get("/api/mission-control/pending-approvals").then((r) => r.data);
export const mcApproveOrg = (id) => api.post(`/api/mission-control/orgs/${id}/approve`).then((r) => r.data);
export const mcRejectOrg = (id, reason) => api.post(`/api/mission-control/orgs/${id}/reject`, { reason }).then((r) => r.data);
export const mcAudit = (limit = 100) => api.get("/api/mission-control/audit", { params: { limit } }).then((r) => r.data);
export const mcSeedDemo = (force = false) => api.post(`/api/mission-control/seed-demo?force=${force}`).then((r) => r.data);

// ---------------- NOTIFICATIONS ----------------
export const getNotifications = () => api.get("/api/notifications").then((r) => r.data);
export const markNotificationsRead = () => api.post("/api/notifications/read-all").then((r) => r.data);

export default api;
